import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { ErrorBoundary, Loading } from '@/components/common';
import {
  AudioGuard,
  CustomerAuthGuard,
  InterstitialGuard,
  ProtectedRouteGuard,
  UserAuthGuard,
} from '@/components/guards';
import {
  DashboardLayout,
  OnboardingLayout,
  StaffLayout,
} from '@/components/layouts';

import { UserWebSocketProvider } from './providers';

const CustomersPage = lazy(() =>
  import('@/pages/dashboard/CustomersPage/CustomersPage').then((module) => ({
    default: module.CustomersPage,
  }))
);
const DashboardHomePage = lazy(() =>
  import('@/pages/dashboard/HomePage/HomePage').then((module) => ({
    default: module.HomePage,
  }))
);

const MenuPage = lazy(() =>
  import('@/pages/dashboard/MenuPage/MenuPage').then((module) => ({
    default: module.MenuPage,
  }))
);
const OrdersPage = lazy(() =>
  import('@/pages/dashboard/OrdersPage/OrdersPage').then((module) => ({
    default: module.OrdersPage,
  }))
);

const BusinessSettingsPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/BusinessSettingsPage').then(
    (module) => ({
      default: module.BusinessSettingsPage,
    })
  )
);
const OutletsPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/OutletsPage').then((module) => ({
    default: module.OutletsPage,
  }))
);
const OrderingSettingsPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/OrderingSettingsPage').then(
    (module) => ({
      default: module.OrderingSettingsPage,
    })
  )
);
const BillingSettingsPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/BillingSettingsPage').then(
    (module) => ({
      default: module.BillingSettingsPage,
    })
  )
);
const NotificationSettingsPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/NotificationSettingsPage').then(
    (module) => ({
      default: module.NotificationSettingsPage,
    })
  )
);
const SubscriptionPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/SubscriptionPage').then(
    (module) => ({
      default: module.SubscriptionPage,
    })
  )
);
const SubscriptionPlansPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/SubscriptionPlansPage').then(
    (module) => ({
      default: module.SubscriptionPlansPage,
    })
  )
);
const SubscriptionComparisonPage = lazy(() =>
  import('@/pages/dashboard/SettingsPage/pages/SubscriptionComparisonPage').then(
    (module) => ({
      default: module.SubscriptionComparisonPage,
    })
  )
);

const TablesPage = lazy(() =>
  import('@/pages/dashboard/TablesPage/TablesPage').then((module) => ({
    default: module.TablesPage,
  }))
);
const UsersPage = lazy(() =>
  import('@/pages/dashboard/UsersPage/UsersPage').then((module) => ({
    default: module.UsersPage,
  }))
);
const ProfilePage = lazy(() =>
  import('@/pages/dashboard/ProfilePage/ProfilePage').then((module) => ({
    default: module.ProfilePage,
  }))
);

const SelectorPage = lazy(() =>
  import('@/components/common/BusinessOutletSelector').then((module) => ({
    default: module.BusinessOutletSelector,
  }))
);

const LoginPage = lazy(() =>
  import('@/pages/onboarding/LoginPage/LoginPage').then((module) => ({
    default: module.LoginPage,
  }))
);
const OnboardingWizard = lazy(() =>
  import('@/pages/onboarding/OnboardingWizard/OnboardingWizard').then(
    (module) => ({
      default: module.OnboardingWizard,
    })
  )
);

// const SidebarDemoPage = lazy(() =>
//   import('@/pages/dashboard/SidebarDemoPage/SidebarDemoPage').then(
//     (module) => ({
//       default: module.SidebarDemoPage,
//     })
//   )
// );

// const ResponsiveDrawerDemoPage = lazy(() =>
//   import('@/pages/dashboard/ResponsiveDrawerDemoPage/ResponsiveDrawerDemoPage').then(
//     (module) => ({
//       default: module.ResponsiveDrawerDemoPage,
//     })
//   )
// );

// Lazy load business pages
const BusinessBasePage = lazy(() =>
  import('@/pages/business/BasePage/BasePage').then((module) => ({
    default: module.BasePage,
  }))
);
const BusinessHomePage = lazy(() =>
  import('@/pages/business/HomePage/HomePage').then((module) => ({
    default: module.HomePage,
  }))
);
const BusinessMenuPage = lazy(() =>
  import('@/pages/business/MenuPage/MenuPage').then((module) => ({
    default: module.MenuPage,
  }))
);

// Lazy load staff pages
const StaffBillingPage = lazy(() =>
  import('@/pages/staff/BillingPage/BillingPage').then((module) => ({
    default: module.BillingPage,
  }))
);
const StaffDeliveryPage = lazy(() =>
  import('@/pages/staff/DeliveryPage/DeliveryPage').then((module) => ({
    default: module.DeliveryPage,
  }))
);
const StaffHomePage = lazy(() =>
  import('@/pages/staff/HomePage/HomePage').then((module) => ({
    default: module.HomePage,
  }))
);
const StaffKitchenPage = lazy(() =>
  import('@/pages/staff/KitchenPage/KitchenPage').then((module) => ({
    default: module.KitchenPage,
  }))
);
const StaffWaiterPage = lazy(() =>
  import('@/pages/staff/WaiterPage/WaiterPage').then((module) => ({
    default: module.WaiterPage,
  }))
);

// Lazy load customer Page
const CustomerLoginPage = lazy(() =>
  import('@/pages/onboarding/CustomerLoginPage/CustomerLoginPage').then(
    (module) => ({
      default: module.CustomerLoginPage,
    })
  )
);

// Loading component for lazy routes with error boundary
const LazyRouteWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ErrorBoundary>
    <Suspense fallback={<Loading />}>{children}</Suspense>
  </ErrorBoundary>
);

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="*" element={<Navigate to="/login" replace />} />
      {/* <Route
        path="/sidebar-demo/*"
        element={
          <LazyRouteWrapper>
            <SidebarDemoPage />
          </LazyRouteWrapper>
        }
      />
      <Route
        path="/responsive-drawer-demo"
        element={
          <LazyRouteWrapper>
            <ResponsiveDrawerDemoPage />
          </LazyRouteWrapper>
        }
      /> */}
      <Route
        element={
          // Guard for checking the route being is accessible to auth/unauth users.
          // This may contain public routes that anyone can access - ex - Login Page.
          <UserAuthGuard>
            <Outlet />
          </UserAuthGuard>
        }
      >
        {/* Public routes */}
        <Route
          path="login"
          element={
            <LazyRouteWrapper>
              <LoginPage />
            </LazyRouteWrapper>
          }
        />

        <Route
          element={
            <OnboardingLayout>
              <Outlet />
            </OnboardingLayout>
          }
        >
          <Route
            path="onboarding"
            element={
              <LazyRouteWrapper>
                <OnboardingWizard />
              </LazyRouteWrapper>
            }
          />
        </Route>
        <Route
          path="user"
          element={
            <ProtectedRouteGuard pageKey="user">
              <LazyRouteWrapper>
                <Outlet />
              </LazyRouteWrapper>
            </ProtectedRouteGuard>
          }
        >
          <Route
            path="*"
            element={
              <LazyRouteWrapper>
                <SelectorPage />
              </LazyRouteWrapper>
            }
          />
          <Route index element={<Navigate to="select" replace />} />
          <Route
            path="select"
            element={
              <LazyRouteWrapper>
                <SelectorPage />
              </LazyRouteWrapper>
            }
          />
        </Route>
        {/* Dashboard View with nested routes */}
        <Route
          path="dashboard"
          element={
            <ErrorBoundary>
              <ProtectedRouteGuard pageKey="dashboard">
                <UserWebSocketProvider>
                  <InterstitialGuard selectOutlet>
                    <DashboardLayout>
                      <Outlet />
                    </DashboardLayout>
                  </InterstitialGuard>
                </UserWebSocketProvider>
              </ProtectedRouteGuard>
            </ErrorBoundary>
          }
        >
          {/* Nested dashboard routes */}
          <Route index element={<Navigate to="/dashboard/home" replace />} />
          <Route
            path="home"
            element={
              <ProtectedRouteGuard pageKey="dashboard.home">
                <LazyRouteWrapper>
                  <DashboardHomePage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />
          <Route
            path="users"
            element={
              <ProtectedRouteGuard pageKey="dashboard.users">
                <LazyRouteWrapper>
                  <UsersPage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />
          <Route
            path="orders"
            element={
              <ProtectedRouteGuard pageKey="dashboard.orders">
                <LazyRouteWrapper>
                  <OrdersPage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />
          <Route
            path="customers"
            element={
              <ProtectedRouteGuard pageKey="dashboard.customers">
                <LazyRouteWrapper>
                  <CustomersPage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />

          <Route
            path="outlet/tables"
            element={
              <ProtectedRouteGuard pageKey="dashboard.outlet.tables">
                <LazyRouteWrapper>
                  <TablesPage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />
          <Route
            path="outlet/menu"
            element={
              <ProtectedRouteGuard pageKey="dashboard.outlet.menu">
                <LazyRouteWrapper>
                  <MenuPage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />
          <Route
            path="profile"
            element={
              <LazyRouteWrapper>
                <ProfilePage />
              </LazyRouteWrapper>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRouteGuard pageKey="dashboard.settings">
                <LazyRouteWrapper>
                  <Outlet />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          >
            <Route
              index
              element={<Navigate to="/dashboard/settings/business" replace />}
            />
            <Route
              path="business"
              element={
                <ProtectedRouteGuard pageKey="dashboard.settings.business">
                  <LazyRouteWrapper>
                    <BusinessSettingsPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />
            <Route
              path="outlets"
              element={
                <ProtectedRouteGuard pageKey="dashboard.settings.outlets">
                  <LazyRouteWrapper>
                    <OutletsPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />
            <Route
              path="ordering"
              element={
                <ProtectedRouteGuard pageKey="dashboard.settings.ordering">
                  <LazyRouteWrapper>
                    <OrderingSettingsPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />
            <Route
              path="tax"
              element={
                <ProtectedRouteGuard pageKey="dashboard.settings.tax">
                  <LazyRouteWrapper>
                    <BillingSettingsPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />
            <Route
              path="notifications"
              element={
                <ProtectedRouteGuard pageKey="dashboard.settings.notifications">
                  <LazyRouteWrapper>
                    <NotificationSettingsPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />
            <Route path="subscriptions">
              <Route
                index
                element={
                  <ProtectedRouteGuard pageKey="dashboard.settings.subscriptions">
                    <LazyRouteWrapper>
                      <SubscriptionPage />
                    </LazyRouteWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="plans"
                element={
                  <ProtectedRouteGuard pageKey="dashboard.settings.subscriptions">
                    <LazyRouteWrapper>
                      <SubscriptionPlansPage />
                    </LazyRouteWrapper>
                  </ProtectedRouteGuard>
                }
              />
              <Route
                path="compare"
                element={
                  <ProtectedRouteGuard pageKey="dashboard.settings.subscriptions">
                    <LazyRouteWrapper>
                      <SubscriptionComparisonPage />
                    </LazyRouteWrapper>
                  </ProtectedRouteGuard>
                }
              />
            </Route>
          </Route>
        </Route>

        {/* Staff Routes */}
        <Route
          path="staff"
          element={
            <ErrorBoundary>
              <ProtectedRouteGuard pageKey="staff">
                <UserWebSocketProvider>
                  <StaffLayout>
                    <Outlet />
                  </StaffLayout>
                </UserWebSocketProvider>
              </ProtectedRouteGuard>
            </ErrorBoundary>
          }
        >
          <Route index element={<Navigate to="/staff/home" replace />} />

          {/* Protected Area that requires Outlet Selection */}
          <Route
            element={
              <InterstitialGuard selectOutlet>
                <Outlet />
              </InterstitialGuard>
            }
          >
            {/* Staff Home Page */}
            <Route
              path="home"
              element={
                <ProtectedRouteGuard pageKey="staff.home">
                  <LazyRouteWrapper>
                    <StaffHomePage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />

            {/* Kitchen Page */}
            <Route
              path="kitchen"
              element={
                <ProtectedRouteGuard pageKey="staff.kitchen">
                  <AudioGuard>
                    <LazyRouteWrapper>
                      <StaffKitchenPage />
                    </LazyRouteWrapper>
                  </AudioGuard>
                </ProtectedRouteGuard>
              }
            />

            {/* Billing Page */}
            <Route
              path="billing"
              element={
                <ProtectedRouteGuard pageKey="staff.billing">
                  <LazyRouteWrapper>
                    <StaffBillingPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />

            {/* Waiter Page */}
            <Route
              path="waiter"
              element={
                <ProtectedRouteGuard pageKey="staff.waiter">
                  <AudioGuard>
                    <LazyRouteWrapper>
                      <StaffWaiterPage />
                    </LazyRouteWrapper>
                  </AudioGuard>
                </ProtectedRouteGuard>
              }
            />

            {/* Delivery Page */}
            <Route
              path="delivery"
              element={
                <ProtectedRouteGuard pageKey="staff.delivery">
                  <LazyRouteWrapper>
                    <StaffDeliveryPage />
                  </LazyRouteWrapper>
                </ProtectedRouteGuard>
              }
            />
          </Route>

          {/* Staff Profile Page */}
          <Route
            path="profile"
            element={
              <ProtectedRouteGuard pageKey="staff.profile">
                <LazyRouteWrapper>
                  <ProfilePage />
                </LazyRouteWrapper>
              </ProtectedRouteGuard>
            }
          />
        </Route>
      </Route>

      {/* Business public routes  b = business*/}
      <Route
        path="/b"
        element={
          <ErrorBoundary>
            <LazyRouteWrapper>
              <BusinessBasePage />
            </LazyRouteWrapper>
          </ErrorBoundary>
        }
      >
        <Route
          path=":businessId"
          element={
            <LazyRouteWrapper>
              <BusinessHomePage />
            </LazyRouteWrapper>
          }
        />
        <Route
          path=":businessId/login"
          element={
            <ErrorBoundary>
              <LazyRouteWrapper>
                <CustomerLoginPage />
              </LazyRouteWrapper>
            </ErrorBoundary>
          }
        />
        <Route
          path=":businessId/o/:outletId"
          element={
            <LazyRouteWrapper>
              <CustomerAuthGuard>
                <BusinessMenuPage />
              </CustomerAuthGuard>
            </LazyRouteWrapper>
          }
        />
        <Route
          path=":businessId/o/:outletId/t/:tableId"
          element={
            <LazyRouteWrapper>
              <CustomerAuthGuard>
                <BusinessMenuPage />
              </CustomerAuthGuard>
            </LazyRouteWrapper>
          }
        />
        <Route
          path=":businessId/o/:outletId/login"
          element={
            <ErrorBoundary>
              <LazyRouteWrapper>
                <CustomerLoginPage />
              </LazyRouteWrapper>
            </ErrorBoundary>
          }
        />
      </Route>
    </Routes>
  );
};
